import headerObject from '@/types/headerObj';
import apiUrl from './api';
import { postWithAuth } from '@/lib/httpAdapter';
import { GitProvider } from '@/types/providerUser';

const localHost = apiUrl;

// ============================================================================
// Setup Team Mapping
// ============================================================================

export interface ClaudeCodeTeamMapping {
  backend_user_id: string;
  claude_code_user_id: string;
  user_email: string;
}

export interface ClaudeCodeSetupTeamMappingRequest {
  org_uuid: string;
  team_uuid?: string;
  /** @deprecated Use standard_api_key / admin_api_key instead. */
  claude_code_api_key?: string;
  claude_code_org_id?: string;
  /** Standard (non-admin) Claude API key – enables member sync. */
  standard_api_key?: string;
  /** Admin / platform Claude API key – enables analytics & API-key attribution. */
  admin_api_key?: string;
}

export interface MappingError {
  user_email: string;
  error: string;
}

export interface MappingStatus {
  total_mappings: number;
  successful_mappings: number;
  failed_mappings: number;
  errors: MappingError[];
}

export interface SyncConfig {
  last_sync: string;
  next_sync: string;
  sync_frequency: string;
}

export interface ClaudeCodeSetupTeamMappingResponse {
  success: boolean;
  message: string;
  error?: string;
}

const setupClaudeCodeTeamMapping = async (
  request: ClaudeCodeSetupTeamMappingRequest,
  headerObj?: headerObject
): Promise<ClaudeCodeSetupTeamMappingResponse | undefined> => {
  if (!request.org_uuid) {
    console.error('Missing required parameter: org_uuid');
    return undefined;
  }

  try {
    const res = await postWithAuth(
      localHost + 'claudeCode/setupTeamMapping/',
      request,
      {
        headers: {
          ...headerObj,
        },
      }
    );
    return await res.json();
  } catch (err: any) {
    console.error('Error setting up Claude Code team mapping:', err);
    throw err;
  }
};

// ============================================================================
// Sync User Mappings
// ============================================================================

export interface SyncOptions {
  sync_new_users: boolean;
  sync_deleted_users: boolean;
  update_existing: boolean;
}

export interface ClaudeCodeSyncUserMappingsRequest {
  org_uuid: string;
  team_uuid?: string;
}

export interface SyncResults {
  users_synced: number;
  new_users_added: number;
  users_updated: number;
  users_removed: number;
  sync_duration: string;
}

export interface ClaudeCodeMappingStatus {
  total_mappings: number;
  active_mappings: number;
  inactive_mappings: number;
}

export interface ClaudeCodeSyncUserMappingsResponse {
  success: boolean;
  mappings_created: number;
  message: string;
  error?: string;
}

const syncClaudeCodeUserMappings = async (
  request: ClaudeCodeSyncUserMappingsRequest,
  headerObj?: headerObject
): Promise<ClaudeCodeSyncUserMappingsResponse | undefined> => {
  if (!request.org_uuid) {
    console.error('Missing required parameter: org_uuid');
    return undefined;
  }

  try {
    const res = await postWithAuth(
      localHost + 'claudeCode/syncUserMappings/',
      request,
      {
        headers: {
          ...headerObj,
        },
      }
    );
    return await res.json();
  } catch (err) {
    console.error('Error syncing Claude Code user mappings:', err);
    throw err;
  }
};

// ============================================================================
// Validate Configuration
// ============================================================================

export interface ClaudeCodeValidateConfigRequest {
  org_uuid: string;
  team_uuid: string;
}

export interface ValidationResults {
  connection_status: string;
  api_access: boolean;
  mappings_valid: boolean;
  permissions_valid: boolean;
}

export interface ConfigurationDetails {
  claude_code_team_id: string;
  api_endpoint: string;
  sync_enabled: boolean;
  last_successful_sync: string;
}

export interface Recommendation {
  type: string;
  message: string;
  priority: string;
}

export interface ClaudeCodeValidateConfigResponse {
  valid: boolean;
  team_members_count: number;
  team_members: Array<{
    id: string;
    name: string;
    email: string;
    role: string;
  }>;
  error?: string;
}

const validateClaudeCodeConfig = async (
  request: ClaudeCodeValidateConfigRequest,
  headerObj?: headerObject
): Promise<ClaudeCodeValidateConfigResponse | undefined> => {
  if (!request.org_uuid || !request.team_uuid) {
    console.error('Missing required parameters: org_uuid and team_uuid');
    return undefined;
  }

  try {
    const res = await postWithAuth(
      localHost + 'claudeCode/validateConfig/',
      request,
      {
        headers: {
          ...headerObj,
        },
      }
    );
    return await res.json();
  } catch (err) {
    console.error('Error validating Claude Code config:', err);
    throw err;
  }
};

// ============================================================================
// Delete API Key
// ============================================================================

export interface ClaudeCodeDeleteApiKeyRequest {
  org_uuid: string;
  team_uuid?: string;
  /** When set, delete only the specified credential type; omit to delete both. */
  credential_type?: 'standard' | 'admin';
}

export interface ClaudeCodeDeleteApiKeyResponse {
  success: boolean;
  message: string;
  error?: string;
}

const deleteClaudeCodeApiKey = async (
  request: ClaudeCodeDeleteApiKeyRequest,
  headerObj?: headerObject
): Promise<ClaudeCodeDeleteApiKeyResponse | undefined> => {
  if (!request.org_uuid) {
    console.error('Missing required parameter: org_uuid');
    return undefined;
  }

  try {
    const res = await postWithAuth(
      localHost + 'claudeCode/deleteApiKey/',
      request,
      {
        headers: {
          ...headerObj,
        },
      }
    );
    return await res.json();
  } catch (err: any) {
    console.error('Error deleting Claude Code API key:', err);
    throw err;
  }
};

// ============================================================================
// Claude Code User Mapping
// ============================================================================

export interface ClaudeCodeUser {
  email: string;
  name: string;
}

export interface BackendUser {
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  github_username?: string;
  gitlab_username?: string;
  bitbucket_username?: string;
  is_mapped: boolean;
}

export interface ExistingMapping {
  mapping_id: number;
  claude_code_user_email: string;
  backend_username: string;
  backend_user_name?: string;
  platform: GitProvider | string;
}

export interface ClaudeCodeUserMappingRequest {
  org_uuid: string;
  action: 'list' | 'create' | 'delete' | 'update';
  backend_username?: string;
  claude_code_user_email?: string;
  platform?: GitProvider;
  mapping_id?: number;
  /** `action: 'list'` only, for the paged tab. Offset into the tool directory. */
  offset?: number;
  /** `action: 'list'` only. Server defaults to 100 and clamps to 200. */
  limit?: number;
  /** `action: 'list'` only. Substring match over the tool user's name and email. */
  search?: string;
}

export interface ClaudeCodeUserMappingResponse {
  claude_code_users: ClaudeCodeUser[];
  backend_users: BackendUser[];
  existing_mappings: ExistingMapping[];
  success: boolean;
  message?: string;
  error?: string;
  /** @deprecated Prefer standard_key_configured / admin_key_configured. */
  claude_configured?: boolean;
  claude_org_id?: string | null;
  /** True when the org has a standard (non-admin) Claude key stored. */
  standard_key_configured?: boolean;
  /** True when the org has an admin / platform Claude key stored. */
  admin_key_configured?: boolean;
  /** True when Claude analytics are available (admin key present). */
  analytics_ready?: boolean;
  /** True when Claude member sync can run (any key configured). */
  member_sync_ready?: boolean;
  /** Count of the filtered directory, present when the list call was paged. */
  total?: number;
  /** True when more rows exist past this window. */
  has_more?: boolean;
}

/**
 * A deep first window so the tab fills in one request, then shallower ones as
 * the reader scrolls. The server caps `limit` at 200.
 */
export const CLAUDE_CODE_USERS_FIRST_LIMIT = 100;
export const CLAUDE_CODE_USERS_NEXT_LIMIT = 100;

const manageClaudeCodeUserMappings = async (
  request: ClaudeCodeUserMappingRequest,
  headerObj?: headerObject
): Promise<ClaudeCodeUserMappingResponse | undefined> => {
  if (!request.org_uuid) {
    console.error('Missing required parameter: org_uuid');
    return undefined;
  }

  try {
    const res = await postWithAuth(
      localHost + 'claudeCode/userMapping/',
      request,
      {
        headers: {
          ...headerObj,
        },
      }
    );
    return await res.json();
  } catch (err: any) {
    console.error('Error managing claude code user mappings:', err);
    throw err;
  }
};

export {
  setupClaudeCodeTeamMapping,
  syncClaudeCodeUserMappings,
  validateClaudeCodeConfig,
  deleteClaudeCodeApiKey,
  manageClaudeCodeUserMappings,
};
